import { z } from "zod";
import { FIELD_NAME_MESSAGE } from "../form-controls/field.js";
import { ModuleFieldSchema, } from "../form-controls/index.js";
import { instancesOf } from "../form-engine/instances.js";
/**
 * Every binding, in the order the builder offers them. `session.reference` is
 * ICIS's APP number, which ICIS labels Session ID; it is not the session's id.
 */
export const BINDINGS = {
    "session.reference": {
        label: "Session ID",
        description: "ICIS's reference for the session, its APP number.",
        kind: "text",
        control: "singleLineText",
        example: "APP-0000012345",
        formMode: "displayOnly",
        contexts: ["session"],
    },
    "session.dssId": {
        label: "DSS Id",
        description: "The id DSS gives the session once it has been sent.",
        kind: "text",
        control: "singleLineText",
        example: "APP-0000012345",
        formMode: "displayOnly",
        contexts: ["session"],
    },
    "session.status": {
        label: "Status",
        description: "Whether the session is scheduled, completed or cancelled.",
        kind: "text",
        control: "singleLineText",
        example: "Completed",
        formMode: "displayOnly",
        contexts: ["session"],
    },
    "session.scheduledStart": {
        label: "Scheduled start",
        description: "When the session is booked to start.",
        kind: "dateTime",
        control: "dateTimePicker",
        example: "2026-11-03T02:00:00.000Z",
        formMode: "changeable",
        required: true,
        contexts: ["session"],
    },
    "session.scheduledEnd": {
        label: "Scheduled end",
        description: "When the session is booked to end.",
        kind: "dateTime",
        control: "dateTimePicker",
        example: "2026-11-03T03:00:00.000Z",
        formMode: "changeable",
        required: true,
        contexts: ["session"],
    },
    "session.interpreterUsed": {
        label: "Interpreter used",
        description: "Whether an interpreter took part in the session.",
        kind: "yesNo",
        control: "toggleSwitch",
        example: true,
        formMode: "changeable",
        required: false,
        contexts: ["session"],
    },
    "session.anonymousClients": {
        label: "Number of anonymous clients",
        description: "How many clients took part without being identified.",
        kind: "number",
        control: "number",
        example: 3,
        formMode: "changeable",
        required: false,
        contexts: ["session"],
    },
};
const BindingKeySchema = z.enum(Object.keys(BINDINGS));
/**
 * A field on a module whose value comes from a binding rather than from whoever
 * fills the form in (ADR-039). It holds what it is bound to, a label only when
 * the administrator has renamed it, and whether the administrator has made it
 * Required, where its binding leaves that to them.
 */
export const BoundFieldSchema = z
    .object({
    id: z.guid(),
    binding: BindingKeySchema,
    label: z.string().trim().min(1, FIELD_NAME_MESSAGE).optional(),
    required: z.boolean().optional(),
})
    .refine(({ binding, required }) => {
    const definition = BINDINGS[binding];
    return (required === undefined ||
        (definition.formMode === "changeable" && !definition.required));
}, {
    message: "Only a field whose binding leaves Required to the form admin can be made Required",
    path: ["required"],
});
/** A field as a module holds it: built from a control, or bound. */
export const ModuleDefinitionFieldSchema = z.union([
    ModuleFieldSchema,
    BoundFieldSchema,
]);
export const isBoundField = (field) => "binding" in field;
/** A bound field the practitioner changes, which is saved back to its record. */
export const isChangeableField = (field) => isBoundField(field) && BINDINGS[field.binding].formMode === "changeable";
/** A bound field only shown, never changed on the form. */
export const isDisplayOnlyField = (field) => isBoundField(field) && BINDINGS[field.binding].formMode === "displayOnly";
/**
 * A bound field whose record makes it Required, since the record always holds
 * a value: locked on in the builder, and never cleared by a form.
 */
export const isRequiredByRecord = (field) => isChangeableField(field) && BINDINGS[field.binding].required;
/**
 * A bound field drawn Required: by its record, or by the form admin, where its
 * binding leaves Required to them.
 */
export const isRequiredBoundField = (field) => isRequiredByRecord(field) ||
    (isChangeableField(field) && field.required === true);
/**
 * What a bound field is called: what it is bound to, until the administrator
 * renames it.
 */
export const boundFieldName = (field) => field.label?.trim() || BINDINGS[field.binding].label;
/** The control a field is drawn with: its own, or its binding's. */
export const controlOf = (field) => isBoundField(field) ? BINDINGS[field.binding].control : field.control;
export const isCustomField = (field) => !isBoundField(field);
/**
 * The bindings a module with this entity link can hold: those for the record
 * its answers are about. A link with no meaning yet holds none, and one the
 * registry doesn't know throws, as `instancesOf` does.
 */
export function bindingsFor(entityLink) {
    const instances = instancesOf(entityLink);
    if (instances === "notSupported")
        return [];
    return Object.keys(BINDINGS).filter((key) => BINDINGS[key].contexts.includes(instances.of));
}
